import type { ActivatedClient, DemoHistoryJob, ImportResult } from '../index.js'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AccountsAdminApi } from '../../accounts/index.js'
import type { AddOnsAdminApi } from '../../add-ons/index.js'
import type { BehaviorsAdminApi } from '../../behaviors/index.js'
import type { ScoringAdminApi } from '../../scoring/index.js'
import { generatePartnerKey, importSigningKey, signProofOfPossession, type PartnerKeyPair } from '../../partner/index.js'
import { requestPartnerToken, requestRanking, sendBehaviorEvent, sendCallout, sendNoShow, sendShiftComplete, type PartnerSession } from '../../partner/index.js'
import {
  DEMO_BEHAVIOR_OVERRIDES,
  DEMO_CUSTOM_BEHAVIOR,
  DEMO_MASTER_SETTINGS,
  DEMO_REQUIRED_SCOPES,
  buildDemoBehaviorEvents,
  buildDemoRankingRequests,
  type DemoOutcomeEvent,
  buildDemoFacilitiesJsonFiles,
  buildDemoFacilityConfigsJsonFiles,
  buildDemoWorkersCsvFiles,
  demoLoginDomain,
} from '../demo/demoData.js'
import { demoExportFacilityId, demoPlanFor, demoSummaryWorker, demoWorkerCount, type DemoChoice, type DemoPlan } from '../demo/demoPlan.js'
import { isBlankPlan } from '../demo/demoRules.js'
import { buildDemoHistoryInput, historyMonthsDone, runDemoHistory, type HistoryMonthsDone } from '../demo/demoHistory.js'

import { TRAINING_FACILITY, TRAINING_WORKER_IDS, buildTrainingOutcomeEvents } from '../demo/trainingCast.js'
import { exportSummariesAsOperator, generateSummaryAsOperator, loginAsOperator, releaseSummaryAsOperator, setFacilityAddress } from '../demo/demoOperator.js'
import { DEMO_BRIDGE_RATE, DEMO_BRIDGE_REASON, DEMO_BRIDGE_SHIFT, demoBridge, demoBridgeDate, loginAsFacilityAdministrator, openCalloutAsAdministrator } from '../demo/demoBridge.js'
import { pressBriefingAsAdministrator, type DemoBriefingOutcome } from '../demo/demoBriefing.js'
import { buildAndReleaseAsOperator, demoRun, demoRunPeriod, openPreferencesPeriod, savePreferencesAsWorker, seedSwapForApproval } from '../demo/demoRun.js'
import { enrollBridgeDate } from '../../partner/index.js'
import { withRetry } from '../../core/index.js'

/** The admin calls the onboarding run makes. */
export type OnboardingAdminApi = ClientsAdminApi & AccountsAdminApi & BehaviorsAdminApi & ScoringAdminApi & AddOnsAdminApi

export const ONBOARDING_STEPS = [
  { id: 'register', label: 'Register the organization' },
  { id: 'generate-key', label: 'Generate the partner key pair (in this browser)' },
  { id: 'register-key', label: 'Register the public key' },
  { id: 'prove-possession', label: 'Prove possession and activate the client' },
  { id: 'create-operator', label: 'Create the operator login' },
] as const

type DemoStepId =
  | 'demo-operators'
  | 'demo-facilities'
  | 'demo-addresses'
  | 'demo-facility-configs'
  | 'demo-add-ons'
  | 'demo-facility-administrators'
  | 'demo-workers'
  | 'demo-worker-logins'
  | 'demo-behaviors'
  | 'demo-partner-token'
  | 'demo-cast-outcomes'
  | 'demo-behavior-events'
  | 'demo-history'
  | 'demo-rankings'
  | 'demo-summaries'
  | 'demo-bridge'
  | 'demo-run'
  | 'demo-needs-you'
  | 'demo-insights'
  | 'demo-briefing'

export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]['id'] | DemoStepId

interface OnboardingStep {
  id: OnboardingStepId
  label: string
}

/** The history step (demo history plan H1), named for the span and the facilities it covers. */
function historyLabel(plan: DemoPlan): string {
  const span = plan.monthSpan === 1 ? 'this month' : `${plan.monthSpan} months`
  const closes = plan.monthSpan > 1 ? ', and every month before this one closed' : ''
  const ahead = plan.monthsAhead === 1 ? 'next month' : `next ${plan.monthsAhead === 2 ? 'two' : plan.monthsAhead === 3 ? 'three' : 'four'} months`
  return `Write ${span} of history at ${eachActiveFacility(plan)}: each month's census, its seats published from the staffing plan, bid, built at the close, released by the engine and worked${closes}; and the ${ahead} published`
}

/** How the steps about activity name the facilities they cover: all of them, unless the plan has facilities without any. */
function eachActiveFacility(plan: DemoPlan): string {
  if (plan.activeFacilities.length < plan.facilities.length) {
    return `each of the ${plan.activeFacilities.length} facilities with activity`
  }
  return plan.facilities.length === 1 ? 'the facility' : 'each facility'
}

function operatorsLabel(plan: DemoPlan): string {
  return plan.extraOperators.length === 1 ? 'Create a second operator login' : `Create ${plan.extraOperators.length} more operator logins`
}

/**
 * The extra steps a demo client gets, after the ones above - fake data so
 * every screen has something in it. Which of them run, and what they say,
 * follows the plan: a minimum demo has one facility and skips the second
 * operator and behaviors, a large demo keeps its activity to the standard
 * facilities, and the months closed follow the span.
 */
export function demoStepsFor(plan: DemoPlan): readonly OnboardingStep[] {
  if (isBlankPlan(plan)) {
    return []
  }
  const one = plan.facilities.length === 1
  const facilityCount = one ? `the ${plan.facilities[0]?.name} facility` : `${plan.facilities.length} facilities`
  const each = one ? 'the facility' : 'each facility'
  const eachActive = eachActiveFacility(plan)
  const summaryWorker = demoSummaryWorker(plan)
  const bridge = demoBridge(plan)
  const run = demoRun(plan)
  return [
    ...(plan.extraOperators.length > 0 ? [{ id: 'demo-operators', label: operatorsLabel(plan) } as const] : []),
    { id: 'demo-facilities', label: `Create ${facilityCount}` },
    { id: 'demo-addresses', label: `Sign in as the operator and record ${one ? "the facility's" : "each facility's"} address` },
    { id: 'demo-facility-configs', label: `Apply the default configuration to ${each}` },
    ...(plan.addOnFacilities.length > 0
      ? [{ id: 'demo-add-ons', label: plan.addOnFacilities.length < plan.activeFacilities.length ? `Switch the add-ons on for ${plan.addOnFacilities.length} of the ${plan.activeFacilities.length} active facilities, one left off to show the gate` : `Switch the add-ons on for ${each}` } as const]
      : []),
    { id: 'demo-facility-administrators', label: `Create a facility administrator for ${each}` },
    { id: 'demo-workers', label: `Import ${demoWorkerCount(plan)} workers` },
    ...(plan.workerLogins.length > 0 ? [{ id: 'demo-worker-logins', label: `Give ${plan.workerLogins.length === 1 ? 'one worker' : `${plan.workerLogins.length} workers`} a login to the worker app` } as const] : []),
    ...(plan.hasBehaviors ? [{ id: 'demo-behaviors', label: plan.behaviorOverrideFacilityId ? 'Define a custom behavior and give one facility its own settings' : 'Define a custom behavior' } as const] : []),
    { id: 'demo-partner-token', label: 'Sign in as the partner with the new key' },
    ...(plan.includesTrainingCast ? [{ id: 'demo-cast-outcomes', label: `Report the two scripted months of the ${TRAINING_WORKER_IDS.length} at ${TRAINING_FACILITY.name}, as the partner` } as const] : []),
    ...(plan.hasBehaviors ? [{ id: 'demo-behavior-events', label: 'Report custom behavior events' } as const] : []),
    ...(plan.activeFacilities.length > 0 ? [{ id: 'demo-history', label: historyLabel(plan) } as const] : []),
    { id: 'demo-rankings', label: `Ask ${eachActive} to rank a pool` },
    ...(summaryWorker ? [{ id: 'demo-summaries', label: summariesLabel(plan, summaryWorker.name) } as const] : []),
    ...(bridge ? [{ id: 'demo-bridge', label: `Enroll ${bridge.worker.name} for a Clinical Bridge day next week and, as ${bridge.facility.name}'s administrator, open a callout for it` } as const] : []),
    ...(run ? [{ id: 'demo-run', label: `Save shift preferences for ${run.workers.length} workers at ${run.facility.name} as themselves for the month taking them, then build it as the operator and release it` } as const] : []),
    ...(run ? [{ id: 'demo-needs-you', label: `Have two of ${run.facility.name}'s workers of different tiers agree a swap, so the Dashboard's Today tab opens with one to approve` } as const] : []),
    ...(plan.activeFacilities.length > 0 ? [{ id: 'demo-insights', label: `Add up this month and last for ${eachActive} in Executive Insights, so the Insights page opens with its portfolio` } as const] : []),
    ...(plan.activeFacilities[0] ? [{ id: 'demo-briefing', label: `As ${plan.activeFacilities[0].name}'s administrator, press for an AI briefing and ask it one question, so the demo opens with a briefing to show` } as const] : []),
  ]
}

/** The performance summary step, named for the worker it summarises and the facility it exports. */
function summariesLabel(plan: DemoPlan, workerName: string): string {
  const exportFacility = plan.facilities.find((facility) => facility.facilityId === demoExportFacilityId(plan))
  return exportFacility ? `Generate ${workerName}'s performance summary and export ${exportFacility.name}'s for HR` : `Generate ${workerName}'s performance summary`
}

/** Every step a run of the given kind goes through, in order, for the progress list. */
export function onboardingStepsFor(demo: DemoChoice | null): readonly OnboardingStep[] {
  return demo ? [...ONBOARDING_STEPS, ...demoStepsFor(demoPlanFor(demo))] : ONBOARDING_STEPS
}

/** A login created during onboarding, with its one-time password, for handing over. */
export interface OnboardingCredential {
  role: 'Operator' | 'Facility administrator' | 'Worker'
  name: string
  /** What this login can see - the whole client, or one facility. */
  access: string
  username: string
  password: string
}

export interface OnboardingInput {
  environment: 'sandbox' | 'production'
  organizationName: string
  applicationName: string
  /** Used twice: the registration's contact email and the operator's login username. */
  contactEmail: string
  /** Also recorded as the organization's contact name - the operator created here is that contact. */
  operatorFirstName: string
  operatorLastName: string
  /** Optional. Empty means none on file. */
  contactPhone: string
  scopes: string[]
  kid: string
  /** When set, also fill the client with fake facilities, logins, workers and shifts of that size and span, and mark it deletable. */
  demo: DemoChoice | null
}

/** Everything produced so far - partial while onboarding is running or after it stops partway. */
export interface OnboardingProgress {
  completed: OnboardingStepId[]
  registrationId?: string
  key?: PartnerKeyPair
  client?: ActivatedClient
  operator?: { operatorId: string; email: string; password: string }
  /** Every login created so far with its one-time password - the contact's operator first, then any demo logins. */
  credentials: OnboardingCredential[]
  /** Set as the demo steps complete; counts are of what the API reported importing. */
  demo?: DemoProgress
  /** How far the step running now has got, for a step that makes many calls: so many of so many things. */
  stepCount?: StepCount
}

/** "120 of 316 facilities": the running step's count, shown beside it while it runs. */
export interface StepCount {
  step: OnboardingStepId
  done: number
  total: number
  noun: string
}

export interface DemoProgress {
  facilities?: number
  addresses?: number
  workers?: number
  shifts?: number
  outcomes?: number
  behaviorEvents?: number
  rankings?: number
  closedPeriods?: number
  /** Add-on switches set on: one per add-on per facility the plan names. */
  addOnSwitches?: number
  /** Performance summaries generated: one for the plan's worker, plus one per worker of the exported facility. */
  summaries?: number
  /** The Clinical Bridge callout opened for the demo's worker, and how many were told (the worker alone, as the plan enrolls one). */
  bridgeCallout?: { calloutId: string; facilityName: string; workerName: string; poolSize: number }
  /** Facilities whose census the demo recorded (census response plan F0). */
  censusFacilities?: number
  /** How far the history job has got (demo history plan H1): months written of all it will write, and what it has written. */
  history?: HistoryMonthsDone & { counts: DemoHistoryJob['counts'] }
  /** The month the demo built from the workers' preferences and released (scheduling rules plan phase B). */
  scheduleRun?: { facilityName: string; period: string; workersWithPreferences: number; scheduled: number; assigned: number; contests: number; unfilled: number }
  /** The Executive Insights months added up on request at the end (executive insights plan section 7): facilities and rollups written. */
  insights?: { facilities: number; rollups: number }
  briefing?: DemoBriefingOutcome
}

export class OnboardingError extends Error {
  constructor(
    message: string,
    public readonly failedStep: OnboardingStepId,
    public readonly progress: OnboardingProgress,
  ) {
    super(message)
    this.name = 'OnboardingError'
  }
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Same convention as the onboarding CLI and the key inventory in docs/onboarding/reliability-m2m-oauth-onboarding.md, e.g. "shifthappens-prod-2026-10". */
export function defaultKid(organizationName: string, environment: 'sandbox' | 'production', now: Date = new Date()): string {
  const slug = slugify(organizationName)
  if (!slug) {
    return ''
  }
  const envSlug = environment === 'production' ? 'prod' : environment
  return `${slug}-${envSlug}-${now.toISOString().slice(0, 7)}`
}

/**
 * The whole admin-side onboarding in one go, mirroring the CLI's `onboard`
 * command: register, generate a key, register it, prove possession (which
 * issues the clientId), then create an operator whose username is the
 * registration's contact email. Reports progress after every step, and on
 * failure throws an OnboardingError carrying everything produced so far -
 * each run creates a real registration, so the caller must be able to show
 * what already exists (and still hand over a generated key) rather than
 * simply start again.
 */
export async function onboardClient(
  api: OnboardingAdminApi,
  input: OnboardingInput,
  issuer: string,
  onProgress: (progress: OnboardingProgress, running: OnboardingStepId | null) => void,
  /** Where the partner-side demo calls (token, events, rankings) are sent - the same API the admin calls go to. */
  apiBaseUrl: string,
): Promise<OnboardingProgress> {
  let progress: OnboardingProgress = { completed: [], credentials: [] }

  async function step<T>(id: OnboardingStepId, run: (report: ReportProgress) => Promise<T>, record: (result: T) => Partial<OnboardingProgress>): Promise<T> {
    onProgress(progress, id)
    // For a long step to show how far it has got, without ending the step.
    const report: ReportProgress = (partial) => {
      progress = { ...progress, ...partial }
      onProgress(progress, id)
    }
    try {
      const result = await run(report)
      progress = { ...progress, ...record(result), completed: [...progress.completed, id] }
      return result
    } catch (error) {
      throw new OnboardingError(error instanceof Error ? error.message : 'Unexpected error', id, progress)
    }
  }

  const { registrationId } = await step(
    'register',
    () =>
      api.createRegistration({
        environment: input.environment,
        organizationName: input.organizationName,
        applicationName: input.applicationName,
        contactEmail: input.contactEmail,
        contactFirstName: input.operatorFirstName,
        contactLastName: input.operatorLastName,
        ...(input.contactPhone ? { contactPhone: input.contactPhone } : {}),
        ...(input.demo ? { isDemo: true, demoSize: input.demo.size } : {}),
        // A demo's own setup posts events and asks for rankings, so it always gets the scopes for them.
        requestedScopes: input.demo ? [...new Set([...input.scopes, ...DEMO_REQUIRED_SCOPES])] : input.scopes,
      }),
    (result) => ({ registrationId: result.registrationId }),
  )
  const key = await step('generate-key', () => generatePartnerKey(input.kid), (result) => ({ key: result }))
  const { challenge } = await step('register-key', () => api.registerKey(registrationId, key.publicJwk), () => ({}))
  const { clientId } = await step(
    'prove-possession',
    async () => api.submitProof(registrationId, await signProofOfPossession(key, registrationId, challenge, issuer)),
    (result) => ({ client: result }),
  )
  const contactName = `${input.operatorFirstName} ${input.operatorLastName}`
  await step(
    'create-operator',
    () =>
      api.createOperator(registrationId, {
        email: input.contactEmail,
        firstName: input.operatorFirstName,
        lastName: input.operatorLastName,
      }),
    (result) => ({
      operator: { operatorId: result.operatorId, email: result.email, password: result.password },
      credentials: [{ role: 'Operator', name: contactName, access: 'Whole client', username: result.email, password: result.password }],
    }),
  )

  // A blank demo is the client alone: nothing further to set up.
  if (input.demo && !isBlankPlan(demoPlanFor(input.demo))) {
    const plan = demoPlanFor(input.demo)
    await runDemoSteps(api, plan, registrationId, clientId, input.organizationName, apiBaseUrl, step, () => progress)
    await runDemoPartnerSteps(api, plan, clientId, key, issuer, apiBaseUrl, step, () => progress)
  }

  onProgress(progress, null)
  return progress
}

type ReportProgress = (partial: Partial<OnboardingProgress>) => void

/** A step's counter: reports "0 of N" at once, then each count it is told, so a long step is seen moving from its first moment. */
function counter(report: ReportProgress, step: OnboardingStepId, total: number, noun: string): (done: number) => void {
  const tell = (done: number) => report({ stepCount: { step, done, total, noun } })
  tell(0)
  return tell
}
type RunStep = <T>(id: OnboardingStepId, run: (report: ReportProgress) => Promise<T>, record: (result: T) => Partial<OnboardingProgress>) => Promise<T>

function assertFullyImported(result: { imported: number; errors: { error: string }[] }, what: string): void {
  if (result.errors.length > 0) {
    throw new Error(`${result.errors.length} ${what} could not be saved: ${result.errors[0]?.error}`)
  }
}

/**
 * Imports the files one after another - a large demo's records are split
 * across several so each call is short - stopping at the first that does
 * not go in whole, and returns how many records were imported in all.
 */
async function importFiles(files: readonly string[], importFile: (file: string) => Promise<ImportResult>, what: string, onProgress?: (imported: number) => void): Promise<number> {
  let imported = 0
  for (const file of files) {
    const result = await withRetry(() => importFile(file))
    assertFullyImported(result, what)
    imported += result.imported
    onProgress?.(imported)
  }
  return imported
}

/** Setup calls in flight at once. Lambda and on-demand DynamoDB scale sideways, so a large demo's hundreds of facilities go in a fraction of the time one call at a time would take. */
const SETUP_CALL_CONCURRENCY = 16

/**
 * Runs one call per item, a few at a time, and returns the results in the
 * items' order. A demo with a handful of facilities finishes in one batch;
 * one with hundreds would take minutes one call at a time. A failure stops
 * the run after its batch, so what exists is at most a few items ahead of
 * what was reported.
 */
async function mapInBatches<T, R>(items: readonly T[], run: (item: T) => Promise<R>, onProgress?: (done: number) => void): Promise<R[]> {
  let results: R[] = []
  for (let start = 0; start < items.length; start += SETUP_CALL_CONCURRENCY) {
    results = [...results, ...(await Promise.all(items.slice(start, start + SETUP_CALL_CONCURRENCY).map((item) => withRetry(() => run(item)))))]
    onProgress?.(results.length)
  }
  return results
}

/**
 * Everything a demo client needs to be shown immediately: further
 * operators (standard and large only), facilities with the default configuration and
 * an administrator each, a roster, and open shifts - all through the same
 * admin endpoints the manual setup uses. Order matters: the roster and
 * shifts refer to facilities, and a facility's dashboard needs its
 * configuration to load.
 */
async function runDemoSteps(
  api: OnboardingAdminApi,
  plan: DemoPlan,
  registrationId: string,
  clientId: string,
  organizationName: string,
  apiBaseUrl: string,
  step: RunStep,
  current: () => OnboardingProgress,
): Promise<void> {
  const now = new Date()
  // Random rather than derived, so re-running a demo for the same organization never collides on a login email.
  const domain = demoLoginDomain(organizationName, crypto.randomUUID().slice(0, 4))

  if (plan.extraOperators.length > 0) {
    await step(
      'demo-operators',
      async (report) => {
        const created: OnboardingCredential[] = []
        const tick = counter(report, 'demo-operators', plan.extraOperators.length, 'operators')
        // One at a time: there are nine at most.
        for (const { emailName, firstName, lastName } of plan.extraOperators) {
          const operator = await api.createOperator(registrationId, { email: `${emailName}@${domain}`, firstName, lastName })
          created.push({ role: 'Operator', name: `${firstName} ${lastName}`, access: 'Whole client', username: operator.email, password: operator.password })
          tick(created.length)
        }
        return created
      },
      (created) => ({ credentials: [...current().credentials, ...created] }),
    )
  }

  await step(
    'demo-facilities',
    (report) => importFiles(buildDemoFacilitiesJsonFiles(plan), (json) => api.importFacilities(clientId, json), 'facilities', counter(report, 'demo-facilities', plan.facilities.length, 'facilities')),
    (imported) => ({ demo: { ...current().demo, facilities: imported } }),
  )

  // Only an operator may set a facility's address, so this step signs in as
  // the operator login created above rather than using the admin credential.
  await step(
    'demo-addresses',
    async (report) => {
      const operator = current().operator
      if (!operator) {
        throw new Error('The operator login was not created, so no address can be recorded')
      }
      const session = await loginAsOperator(apiBaseUrl, operator.email, operator.password)
      const recorded = await mapInBatches(plan.facilities, (facility) => setFacilityAddress(apiBaseUrl, session, facility.facilityId, facility.address, facility.phone), counter(report, 'demo-addresses', plan.facilities.length, 'addresses'))
      return recorded.length
    },
    (recorded) => ({ demo: { ...current().demo, addresses: recorded } }),
  )

  await step(
    'demo-facility-configs',
    (report) => importFiles(buildDemoFacilityConfigsJsonFiles(plan), (json) => api.importFacilityConfigs(clientId, json), 'facility configurations', counter(report, 'demo-facility-configs', plan.facilities.length, 'configurations')),
    () => ({}),
  )

  // The demo licenses were granted when the client was activated; the switches are set here, a few at a time, through the admin's own path.
  if (plan.addOnFacilities.length > 0) {
    await step(
      'demo-add-ons',
      async (report) => {
        const catalog = await api.listAddOns()
        const switches = catalog.flatMap((addOn) => plan.addOnFacilities.map((facility) => ({ key: addOn.key, facilityId: facility.facilityId })))
        const set = await mapInBatches(switches, ({ key, facilityId }) => api.setFacilityAddOn(clientId, facilityId, key, true), counter(report, 'demo-add-ons', switches.length, 'switches'))
        return set.length
      },
      (set) => ({ demo: { ...current().demo, addOnSwitches: set } }),
    )
  }

  await step(
    'demo-facility-administrators',
    (report) =>
      mapInBatches(plan.facilities, async (facility): Promise<OnboardingCredential> => {
        // The administrator's phone: their own where the data gives one, else the facility's main number, so every demo dashboard names someone reachable.
        const phone = facility.administrator.phone ?? facility.phone
        const administrator = await api.createFacilityAdministrator(clientId, {
          email: `${facility.facilityId}@${domain}`,
          firstName: facility.administrator.firstName,
          lastName: facility.administrator.lastName,
          facilityIds: [facility.facilityId],
          ...(phone ? { phone } : {}),
        })
        return {
          role: 'Facility administrator',
          name: `${facility.administrator.firstName} ${facility.administrator.lastName}`,
          access: facility.name,
          username: administrator.email,
          password: administrator.password,
        }
      }, counter(report, 'demo-facility-administrators', plan.facilities.length, 'administrators')),
    (created) => ({ credentials: [...current().credentials, ...created] }),
  )

  await step(
    'demo-workers',
    (report) => importFiles(buildDemoWorkersCsvFiles(plan, now), (csv) => api.importWorkers(clientId, csv), 'workers', counter(report, 'demo-workers', demoWorkerCount(plan), 'workers')),
    (imported) => ({ demo: { ...current().demo, workers: imported } }),
  )

  // The worker's own seat, so a demo can be shown from it (and the training
  // materials run on Devon's real sign-in). One at a time, as the administrators.
  if (plan.workerLogins.length > 0) {
    await step(
      'demo-worker-logins',
      async (report) => {
        const created: OnboardingCredential[] = []
        const tick = counter(report, 'demo-worker-logins', plan.workerLogins.length, 'logins')
        for (const worker of plan.workerLogins) {
          const facility = plan.facilities.find((candidate) => candidate.facilityId === worker.facilityId)
          const login = await api.workerLogin(clientId, worker.facilityId, worker.workerId).create({ email: `${slugify(worker.name).replace(/-/g, '.')}@${domain}` })
          created.push({ role: 'Worker', name: worker.name, access: facility?.name ?? worker.facilityId, username: login.email, password: login.password })
          tick(created.length)
        }
        return created
      },
      (created) => ({ credentials: [...current().credentials, ...created] }),
    )
  }
}

/**
 * Partner calls in flight at once: a demo's few thousand outcomes go in a
 * few hundred rounds rather than a few thousand. Held at 12 until the
 * account's Lambda concurrency limit is known to allow more - past it,
 * the gateway answers calls with bare errors the retries then absorb.
 */
const PARTNER_CALL_CONCURRENCY = 12

/** Runs the calls a few at a time - fast enough for a few thousand, without opening them all at once - telling `onBatch` how many are done after each batch. */
async function inBatches<T>(items: readonly T[], run: (item: T) => Promise<unknown>, onBatch?: (done: number) => void): Promise<number> {
  for (let start = 0; start < items.length; start += PARTNER_CALL_CONCURRENCY) {
    await Promise.all(items.slice(start, start + PARTNER_CALL_CONCURRENCY).map((item) => withRetry(() => run(item))))
    onBatch?.(Math.min(start + PARTNER_CALL_CONCURRENCY, items.length))
  }
  return items.length
}

/**
 * Like `inBatches`, but items sharing a lane go one after another while
 * the lanes run side by side: a worker's outcomes are sent in order, so
 * no two recalculations of the same worker run at once, while many
 * workers' are sent at the same time. `onProgress` is told the running
 * count as each call finishes.
 */
async function inLanes<T>(items: readonly T[], laneOf: (item: T) => string, run: (item: T) => Promise<unknown>, onProgress?: (done: number) => void): Promise<number> {
  const lanes = new Map<string, T[]>()
  for (const item of items) {
    lanes.set(laneOf(item), [...(lanes.get(laneOf(item)) ?? []), item])
  }
  const queue = [...lanes.values()]
  let done = 0
  async function drain(): Promise<void> {
    for (let lane = queue.shift(); lane !== undefined; lane = queue.shift()) {
      for (const item of lane) {
        await withRetry(() => run(item))
        done += 1
        onProgress?.(done)
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(PARTNER_CALL_CONCURRENCY, queue.length) }, () => drain()))
  return items.length
}

function sendOutcome(apiBaseUrl: string, session: PartnerSession, event: DemoOutcomeEvent): Promise<unknown> {
  switch (event.kind) {
    case 'shift-complete':
      return sendShiftComplete(apiBaseUrl, session, event.payload, event.idempotencyKey)
    case 'callout':
      return sendCallout(apiBaseUrl, session, event.payload, event.idempotencyKey)
    case 'no-show':
      return sendNoShow(apiBaseUrl, session, event.payload, event.idempotencyKey)
  }
}

/**
 * The part of a demo that only a partner can do: its behaviors are set
 * through the admin routes, but outcomes, custom events and rankings are
 * sent as the partner, with a token obtained from the key this run just
 * generated - the same calls a real integration makes. Everything written
 * here is stored under the client, so deleting the demo client removes it.
 * Behaviors and their events run only when the plan includes them; the
 * closes cover every earlier month of the span, oldest first.
 */
async function runDemoPartnerSteps(
  api: OnboardingAdminApi,
  plan: DemoPlan,
  clientId: string,
  key: PartnerKeyPair,
  issuer: string,
  apiBaseUrl: string,
  step: RunStep,
  current: () => OnboardingProgress,
): Promise<void> {
  const now = new Date()

  if (plan.hasBehaviors) {
    await step(
      'demo-behaviors',
      async () => {
        const behaviors = api.behaviorsFor(clientId)
        await behaviors.createCustom(DEMO_CUSTOM_BEHAVIOR)
        await behaviors.saveMaster(DEMO_MASTER_SETTINGS)
        if (plan.behaviorOverrideFacilityId) {
          await behaviors.saveFacility(plan.behaviorOverrideFacilityId, DEMO_BEHAVIOR_OVERRIDES.overrides)
        }
      },
      () => ({}),
    )
  }

  const session = await step(
    'demo-partner-token',
    async () => requestPartnerToken(apiBaseUrl, issuer, clientId, { kid: key.kid, signingKey: await importSigningKey(key.privateJwk) }, ''),
    () => ({}),
  )

  // The training cast's two scripted months at Harbor View go first: the history closes last month there, and the
  // manual's finals are the close of exactly these outcomes. One lane per worker, as each rescores its worker.
  if (plan.includesTrainingCast) {
    await step(
      'demo-cast-outcomes',
      (report) => {
        const events = buildTrainingOutcomeEvents(now)
        return inLanes(
          events,
          (event) => event.payload.worker_id,
          (event) => sendOutcome(apiBaseUrl, session, event),
          counter(report, 'demo-cast-outcomes', events.length, 'shift outcomes sent'),
        )
      },
      (count) => ({ demo: { ...current().demo, outcomes: count } }),
    )
  }

  // Before the history, so the months it closes count them.
  if (plan.hasBehaviors) {
    await step(
      'demo-behavior-events',
      (report) => {
        const events = buildDemoBehaviorEvents(plan, now)
        return inBatches(events, (event) => sendBehaviorEvent(apiBaseUrl, session, event.payload, event.idempotencyKey), counter(report, 'demo-behavior-events', events.length, 'events sent'))
      },
      (count) => ({ demo: { ...current().demo, behaviorEvents: count } }),
    )
  }

  // Every month of the span, lived as a working facility would have (demo history plan H1): written on the API in the
  // background, through its own engines, and followed here. It publishes the months ahead too, closes every month
  // before this one and scores this one, so the census, the outcomes, the closes and the scores are all its.
  const historyInput = buildDemoHistoryInput(plan)
  if (historyInput) {
    await step(
      'demo-history',
      (report) => runDemoHistory(api, clientId, historyInput, (job) => report({ demo: { ...current().demo, history: { ...historyMonthsDone(job), counts: job.counts } } })),
      (job) => ({
        demo: {
          ...current().demo,
          history: { ...historyMonthsDone(job), counts: job.counts },
          shifts: job.counts.shifts,
          outcomes: (current().demo?.outcomes ?? 0) + job.counts.outcomes,
          closedPeriods: job.counts.closes,
          censusFacilities: job.facilities.length,
        },
      }),
    )
  }

  await step(
    'demo-rankings',
    (report) => {
      const requests = buildDemoRankingRequests(plan, now)
      return inBatches(requests, ({ facilityId, request }) => requestRanking(apiBaseUrl, session, facilityId, request), counter(report, 'demo-rankings', requests.length, 'rankings'))
    },
    (count) => ({ demo: { ...current().demo, rankings: count } }),
  )

  // The Performance Summaries add-on, as its operator would use it: one worker's summary to open from the roster, and one
  // facility's export so its receipts list has an entry. Only staff or the HR integration may generate, so this signs in as
  // the operator login this run created. Stored under the client, so it goes with a deleted demo.
  const summaryWorker = demoSummaryWorker(plan)
  if (summaryWorker) {
    await step(
      'demo-summaries',
      async () => {
        const operator = current().operator
        if (!operator) {
          throw new Error('The operator login was not created, so no summary can be generated')
        }
        const session = await loginAsOperator(apiBaseUrl, operator.email, operator.password)
        const generated = await generateSummaryAsOperator(apiBaseUrl, session, summaryWorker.workerId)
        // Released, so the demo's worker login shows the summary on its home screen.
        await releaseSummaryAsOperator(apiBaseUrl, session, summaryWorker.workerId, generated.summaryId)
        const exportFacilityId = demoExportFacilityId(plan)
        const exported = exportFacilityId ? await exportSummariesAsOperator(apiBaseUrl, session, exportFacilityId) : 0
        return 1 + exported
      },
      (generated) => ({ demo: { ...current().demo, summaries: generated } }),
    )
  }

  // Clinical Bridge, as the plan's section 7 asks: the worker with the login is enrolled for a day next week as the
  // partner would record it from its bid screen (plan D12), then the facility's administrator - the only one who may -
  // opens a callout for that day, so the worker's seat opens on a live "Open shift" card and the dashboard on a pool
  // waiting. Nobody answers: that is the demo's to show.
  const bridge = demoBridge(plan)
  if (bridge) {
    await step(
      'demo-bridge',
      async () => {
        const date = demoBridgeDate(bridge.facility, now)
        await enrollBridgeDate(apiBaseUrl, session, bridge.worker.workerId, date)
        const credential = current().credentials.find((entry) => entry.role === 'Facility administrator' && entry.access === bridge.facility.name)
        if (!credential) {
          throw new Error(`The administrator login for ${bridge.facility.name} was not created, so no callout can be opened`)
        }
        const administrator = await loginAsFacilityAdministrator(apiBaseUrl, credential.username, credential.password)
        const callout = await openCalloutAsAdministrator(apiBaseUrl, administrator, bridge.facility.facilityId, {
          shift: { date, start: DEMO_BRIDGE_SHIFT.start, end: DEMO_BRIDGE_SHIFT.end, unitId: DEMO_BRIDGE_SHIFT.unitId, unitName: DEMO_BRIDGE_SHIFT.unitName, role: bridge.worker.role },
          reason: DEMO_BRIDGE_REASON,
          pay: { rate: DEMO_BRIDGE_RATE },
        })
        return { calloutId: callout.calloutId, facilityName: bridge.facility.name, workerName: bridge.worker.name, poolSize: callout.pool.length }
      },
      (opened) => ({ demo: { ...current().demo, bridgeCallout: opened } }),
    )
  }

  // The month, built and released (scheduling rules plan phase B): the facility's workers save preferences as
  // themselves - a demo session each, the only token that may write a worker's own preferences - varied so the run
  // has contests to settle, then the operator builds the month taking preferences and releases it, so every worker seat opens on a
  // released schedule and the Tools tab on a run.
  const run = demoRun(plan)
  if (run) {
    await step(
      'demo-run',
      async () => {
        // The month taking preferences, as the API reckons it in the facility's zone under its rules.
        let period: string | null = null
        let saved = 0
        for (const [index, worker] of run.workers.entries()) {
          const opened = await api.openDemoSession(clientId, { as: 'worker', subject: worker.workerId })
          period ??= (await openPreferencesPeriod(apiBaseUrl, opened.accessToken, worker.workerId)) ?? demoRunPeriod(run.facility, now)
          if (await savePreferencesAsWorker(apiBaseUrl, opened.accessToken, worker.workerId, period, index)) {
            saved += 1
          }
        }
        const operator = current().operator
        if (!operator) {
          throw new Error('The operator login was not created, so the month cannot be built')
        }
        if (!period) {
          throw new Error('No worker could be asked which month is taking preferences')
        }
        const released = await buildAndReleaseAsOperator(apiBaseUrl, await loginAsOperator(apiBaseUrl, operator.email, operator.password), run.facility.facilityId, period)
        return { facilityName: run.facility.name, period, workersWithPreferences: saved, scheduled: released.totals.scheduled, assigned: released.totals.assigned, contests: released.totals.contests, unfilled: released.totals.shiftsUnfilled }
      },
      (built) => ({ demo: { ...current().demo, scheduleRun: built } }),
    )
    // Something to decide on the Today tab (facility operations dashboard plan phase 5): a cross-tier swap held for approval.
    const releasedPeriod = current().demo?.scheduleRun?.period ?? null
    if (releasedPeriod) {
      await step(
        'demo-needs-you',
        async () => seedSwapForApproval(apiBaseUrl, async (workerId) => (await api.openDemoSession(clientId, { as: 'worker', subject: workerId })).accessToken, run.workers, releasedPeriod),
        () => ({}),
      )
    }
  }

  // Executive Insights (executive insights plan section 7): every past month was added up as the history closed it; this
  // month and last are added up now, rather than at the clock's next pass, so the Insights page opens with its portfolio.
  if (plan.activeFacilities.length > 0) {
    await step(
      'demo-insights',
      () => api.recomputeInsights(clientId, { facilityIds: plan.activeFacilities.map((facility) => facility.facilityId) }),
      (added) => ({ demo: { ...current().demo, insights: { facilities: added.facilities, rollups: added.rollups } } }),
    )
  }

  // The AI briefing (AI briefings plan D11): the first active facility's administrator presses for a briefing and asks it
  // one question, so the demo opens with a briefing to show. The write runs in the API's background; the step waits for it.
  const briefingFacility = plan.activeFacilities[0]
  const briefingLogin = briefingFacility ? current().credentials.find((credential) => credential.role === 'Facility administrator' && credential.access === briefingFacility.name) : undefined
  if (briefingFacility && briefingLogin) {
    await step(
      'demo-briefing',
      async () => pressBriefingAsAdministrator(apiBaseUrl, await loginAsFacilityAdministrator(apiBaseUrl, briefingLogin.username, briefingLogin.password), briefingFacility.facilityId),
      (briefing) => ({ demo: { ...current().demo, briefing } }),
    )
  }
}
